import { useGetBatchPipelineStageProjects } from '@/api/queries/pipelines/pipelineStageProjects';
import useGetPipeline from '@/api/queries/pipelines/useGetPipeline';
import { GetPipelineStageProjectsInput } from '@/gql/graphql';
import { getPipelineStageInputLocalStorageKey } from '@/helpers/localStorageKeyHelper';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { useTranslation } from '@/i18n';
import ProjectsContextProvider, {
  ProjectsContext,
} from '@/ui/components/contextProviders/ProjectsContextProvider';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import PipelineStageWithMemory from '@/ui/pages/pipelinePage/components/PipelineStageWithMemory';
import ProjectCardOverlay from '@/ui/pages/pipelinePage/components/ProjectCardOverlay';
import { pointerWithin } from '@dnd-kit/core';
import { Button, Divider, Tooltip } from '@payaca/ambrosia';
import PipelineStage from '@payaca/components/pipelineStage/PipelineStage';
import { DndContext, DragOverlay } from '@payaca/dnd';
import { useLocalStorage, usePersistedScrollPosition } from '@payaca/hooks';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { ListedDealViews } from '@payaca/types/listedDealTypes';
import { clstx } from '@payaca/utilities/cvaMerge';
import { LayoutGroup } from 'motion/react';
import { FC, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import PipelineDisplayType from './components/PipelineDisplayType';
import PipelineNavigationDropdown from './components/PipelineNavigationDropdown';
import PipelineStageFiltering from './components/PipelineStageFiltering';
import PipelineUserSelection from './components/PipelineUserSelection';
import {
  getPipelineScrollPositionKey,
  PIPELINE_SCROLL_POSITION_MAX_AGE_MS,
} from './pipelineScrollPosition';

const PipelinePageContent: FC = () => {
  const translate = useTranslation();
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const canCreateProject = useUserHasPermission({
    permissions: [DealsPermissions.ADD_DEAL, DealsPermissions.ADD_MY_DEAL],
  });
  const canFilterByUser = useUserHasPermission({
    permissions: [DealsPermissions.GET_LISTED_DEALS],
  });
  const { createProject, onUpdate: updateProjectsRequestData } =
    useContext(ProjectsContext);

  const {
    pipeline,
    isLoading: isLoadingPipeline,
    isFetched: hasFetchedPipelineData,
  } = useGetPipeline(pipelineId);

  useEffect(() => {
    // Update the stored pipeline ID, so the right pipeline is selected when
    // you go back to the projects page.
    if (pipeline) {
      updateProjectsRequestData({
        pipelineId: +pipeline.id,
      });
    }
  }, [pipeline, updateProjectsRequestData]);

  const [pipelineStageInput, setPipelineStageInput] =
    useLocalStorage<GetPipelineStageProjectsInput>(
      getPipelineStageInputLocalStorageKey(),
      {}
    );

  const { isFetching: isBatchFetchingPipelineStageProjects } =
    useGetBatchPipelineStageProjects({
      pipelinePublicId: pipelineId,
      input: pipelineStageInput,
    });

  // Scroll-snap on the stages row and dnd-kit's drag autoscroll fight each
  // other on mobile: the snap pulls the active stage back into view mid-drag,
  // making it impossible to drop a card onto the neighbouring stage. Disable
  // snap while a drag is in progress.
  const [isDragging, setIsDragging] = useState(false);

  const stagesScrollRef = usePersistedScrollPosition({
    key: getPipelineScrollPositionKey(pipelineId),
    maxAgeMs: PIPELINE_SCROLL_POSITION_MAX_AGE_MS,
    enabled: hasFetchedPipelineData && !!pipeline,
  });

  return (
    <AuthenticatedPageWrapper className="pipeline-page-override">
      <Helmet title={translate('pages.deals.title')} />
      <div className="h-full flex flex-col">
        <div className="flex justify-between items-center m-4 pb-0">
          <PipelineNavigationDropdown />

          <Tooltip
            enabled={!canCreateProject}
            tooltipContent="You don't have permission to create a project"
          >
            <Button
              disabled={!canCreateProject}
              size="sm"
              onClick={() => createProject(pipeline?.publicId)}
            >
              Create project
            </Button>
          </Tooltip>
        </div>

        <Divider className="m-0 p-0" />

        <div className="p-4 pb-0">
          <PipelineStageFiltering
            isLoading={isBatchFetchingPipelineStageProjects}
            input={pipelineStageInput}
            onChangeInput={setPipelineStageInput}
            extraControls={
              <>
                <Tooltip
                  enabled={!canFilterByUser}
                  tooltipContent="You can only view projects assigned to you"
                >
                  <PipelineUserSelection
                    disabled={!canFilterByUser}
                    input={pipelineStageInput}
                    onChangeInput={setPipelineStageInput}
                  />
                </Tooltip>
                <PipelineDisplayType />
              </>
            }
          />
        </div>

        <DndContext
          collisionDetection={pointerWithin}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => setIsDragging(false)}
          onDragCancel={() => setIsDragging(false)}
        >
          <div
            ref={stagesScrollRef}
            className={clstx(
              'flex gap-4 w-full flex-1 p-4',
              hasFetchedPipelineData && 'overflow-x-auto',
              // On mobile, snap each stage into view as the user swipes
              // horizontally so they only see one stage at a time. Suspended
              // mid-drag so dnd-kit's autoscroll can move freely.
              !isDragging && 'snap-x snap-mandatory md:snap-none'
            )}
          >
            {isLoadingPipeline &&
              Array.from({ length: 6 }).map((_, index) => (
                <PipelineStage.SkeletonLoader
                  key={index}
                  // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Viewport-relative size
                  className="w-[calc(100vw-2rem)] md:w-80 md:min-w-80 shrink-0 snap-center md:snap-align-none"
                />
              ))}

            <LayoutGroup>
              {pipeline?.stages.map((pipeLineStage) => (
                <PipelineStageWithMemory
                  key={pipeLineStage.publicId}
                  pipelinePublicId={pipelineId}
                  pipelineStagePublicId={pipeLineStage.publicId}
                  input={pipelineStageInput}
                />
              ))}
            </LayoutGroup>
          </div>

          <DragOverlay>
            <ProjectCardOverlay />
          </DragOverlay>
        </DndContext>
      </div>
    </AuthenticatedPageWrapper>
  );
};

const PipelinePage: FC = () => {
  return (
    <ProjectsContextProvider initialDisplayType={ListedDealViews.PIPELINE}>
      <PipelinePageContent />
    </ProjectsContextProvider>
  );
};

export default PipelinePage;
